import React from 'react';

import { Box, Sheet, Table, VStack } from '@betterment/samba-react';
import { sortBy } from 'lodash';

import { SecurityGroupPositionSheet } from './SecurityGroupPositionSheet';
import { type AvailableTableColumns, TableCells } from './TableCells/Cells';

import type { SecurityGroupPosition } from './data';

type SecurityGroupPositionsHoldingsContentProps = {
  positions: Array<SecurityGroupPosition>;
  columns: AvailableTableColumns;
  showHeader?: boolean;
  showDrawer?: boolean;
  showSellButton?: boolean;
  shouldSortPositions?: boolean;
  driftThreshold?: number;
  sellButton?: React.ComponentType<{ securityId: string }>;
};

const listRowStyles = (isOpen: boolean, showDrawer?: boolean) => {
  return {
    width: '100%',
    transition: 'background-color 0.2s ease-in-out',
    ...(showDrawer && {
      backgroundColor: isOpen ? 'background.info' : 'background.transparent',
      '&:hover': {
        backgroundColor: 'background.info',
        cursor: 'pointer',
      },
    }),
  };
};

export const SecurityGroupPositionsRow: React.FC<{
  position: SecurityGroupPosition;
  columns: AvailableTableColumns;
  showDrawer: boolean;
  driftThreshold?: number;
  sellButton?: React.ComponentType<{ securityId: string }>;
}> = ({ position, columns, showDrawer, driftThreshold, sellButton }) => {
  const [isOpen, setIsOpen] = React.useState(false);

  return (
    <>
      <Table.Row
        onClick={() => setIsOpen(showDrawer)}
        css={listRowStyles(isOpen, showDrawer)}
        data-testid={`position-row-${position.securityGroup.name}`}
      >
        <TableCells columns={columns} position={position} showDrawer={showDrawer} driftThreshold={driftThreshold} />
      </Table.Row>
      <Sheet overlay="transparent" open={isOpen} onOpenChange={setIsOpen}>
        <SecurityGroupPositionSheet position={position} sellButton={sellButton} />
      </Sheet>
    </>
  );
};

export const SecurityGroupPositionsHoldingsContent: React.FC<SecurityGroupPositionsHoldingsContentProps> = ({
  positions,
  columns,
  showDrawer = true,
  showHeader = false,
  shouldSortPositions = false,
  driftThreshold,
  sellButton,
}) => {
  const lastColumn = columns.length - 1;
  const userPositions = shouldSortPositions ? sortBy(positions, 'currentWeight').reverse() : positions;
  return (
    <Box css={{ px: 'space.300', pb: 'space.300' }}>
      <Table>
        {showHeader && (
          <Table.Header>
            <Table.Row>
              {columns.map((col, i) => (
                <Table.ColumnHeaderCell
                  css={{
                    pl: i === 0 ? 'space.0' : 'space.200',
                    pr: i === lastColumn ? 'space.0' : 'space.200',
                  }}
                  key={col}
                  alignment={i > 0 ? 'end' : 'start'}
                >
                  <VStack>{col}</VStack>
                </Table.ColumnHeaderCell>
              ))}
            </Table.Row>
          </Table.Header>
        )}
        <Table.Body css={{ borderTop: 'token(borderWidths.thin) solid token(colors.border.weak)' }}>
          {userPositions.map(position => (
            <SecurityGroupPositionsRow
              position={position}
              showDrawer={showDrawer}
              columns={columns}
              key={position.id}
              driftThreshold={driftThreshold}
              sellButton={sellButton}
            />
          ))}
        </Table.Body>
      </Table>
    </Box>
  );
};
